import { RefObject } from 'react';

/**
 * Utilitaire pour accéder en toute sécurité à une référence React
 * @param ref La référence React à accéder
 * @param defaultValue Valeur par défaut à retourner si la référence est null/undefined
 * @returns La valeur de la référence ou la valeur par défaut
 */
export function safeRef<T>(ref: RefObject<T>, defaultValue?: T): T | undefined {
  return ref.current ?? defaultValue;
}

/**
 * Utilitaire pour exécuter une fonction sur une référence React si elle existe
 * @param ref La référence React à accéder
 * @param callback Fonction à exécuter avec la valeur de la référence
 * @returns Le résultat de la fonction ou undefined si la référence est null/undefined
 */
export function withRef<T, R>(ref: RefObject<T>, callback: (value: T) => R): R | undefined {
  if (ref.current) {
    return callback(ref.current);
  }
  return undefined;
}

/**
 * Utilitaire pour exécuter une fonction sur une référence React si elle existe,
 * avec une valeur par défaut si la référence est null/undefined
 * @param ref La référence React à accéder
 * @param callback Fonction à exécuter avec la valeur de la référence
 * @param defaultValue Valeur par défaut à retourner si la référence est null/undefined
 * @returns Le résultat de la fonction ou la valeur par défaut
 */
export function withRefOrDefault<T, R>(
  ref: RefObject<T>, 
  callback: (value: T) => R, 
  defaultValue: R
): R {
  return withRef(ref, callback) ?? defaultValue;
}

/**
 * Utilitaire pour exécuter une action sur une référence React si elle existe
 * @param ref La référence React à accéder
 * @param action Action à exécuter avec la valeur de la référence
 * @returns true si l'action a été exécutée, false sinon
 */
export function doWithRef<T>(ref: RefObject<T>, action: (value: T) => void): boolean {
  if (ref.current) {
    action(ref.current);
    return true;
  }
  return false;
}

